import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { GlobalSearchBar } from '@ztechapps/webui.data-tools'
import type { GlobalSearchResult } from '@ztechapps/common.contract'
import { routesDef } from '../../config/routes'

type SearchRouteDef = { altPath: (params: { _id: string }) => string }

// Maps a search result's module to the routesDef entry that opens its detail screen. Keyed on
// `string`, not the module literals, so an unrecognized module types as `undefined` rather than
// relying on a cast to assert it can't happen.
const SEARCH_MODULE_ROUTES: Record<string, SearchRouteDef | undefined> = {
  User: routesDef.TeacherDetailPage,
  School: routesDef.SchoolDetailPage,
  Chat: routesDef.ConversationItemPage,
}

// The group headings: the API names a group by its module, which reads as code to an operator.
const SEARCH_MODULE_LABELS: Record<string, string> = {
  User: 'Teachers',
  School: 'Schools',
  Chat: 'Conversations',
}

export const GlobalSearchWrapper = () => {
  const navigate = useNavigate()

  const handleResultClick = useCallback(
    (result: GlobalSearchResult) => {
      const routeDef = SEARCH_MODULE_ROUTES[result.module]

      if (routeDef?.altPath) {
        // The conversation detail route resolves threads on the external key, never the database
        // key, so Chat rows navigate on it — every other module keeps its database-key URL.
        const key = result.module === 'Chat' ? (result.externalId ?? result._id) : result._id
        navigate(routeDef.altPath({ _id: key }))
      }
    },
    [navigate]
  )

  return (
    <GlobalSearchBar
      onResultClick={handleResultClick}
      placeholder="Search teachers, schools, conversations, emails…"
      moduleLabels={SEARCH_MODULE_LABELS}
    />
  )
}
